import { AuthContext } from '@repo-level/core-front';
import { ErrorBoundary } from 'components/internal/ErrorBoundary';
import { InternalError } from 'components/internal/InternalError';
import type { ModalDefaultProps } from 'components/Modal';
import { Modal } from 'components/Modal';
import { Spinner } from 'components/Spinner';
import {
  addUrlParameter,
  getQueryParameter,
  removeUrlParameters,
} from 'helpers/common';
import { MODAL_LOADERS, ModalType, NON_AUTH_MODALS } from 'helpers/modals';
import { useModalsPreloading } from 'hooks/useModalsPreloading';
import type { FC } from 'react';
import React, {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export { ModalType } from 'helpers/modals';

export const nonAuthModalsMap: string[] = NON_AUTH_MODALS;

type ModalComponent = React.LazyExoticComponent<
  React.ComponentType<ModalDefaultProps>
>;

type ModalsState = {
  name: string;
  modalType?: ModalType;
  component: ModalComponent | React.ComponentType<ModalDefaultProps>;
  props?: Record<string, any>;
};

const PRELOADED_MODALS: ModalType[] = [ModalType.Registration, ModalType.Login];

interface ModalsContextData {
  modalComponent: JSX.Element | null;
  modals: ModalsState[];
}

type HandleModalFn = (
  modal: ModalType | ModalComponent | undefined,
  params?: {
    name?: string;
    stayInSameWindow?: boolean;
    props?: Record<string, any>;
  }
) => () => void;

interface ModalsContextAction {
  handleModal: HandleModalFn;
}

export const ModalsContext = createContext<
  ModalsContextData & ModalsContextAction
>(null as any);

function addBreadcrumbModal(name: string, status: 'open' | 'close') {}

export const ModalsContextProvider: FC = ({ children }) => {
  const { isAuthenticated, pendingUser } = useContext(AuthContext);
  const [modals, setModals] = useState<ModalsState[]>([]);

  const modalsCache = useRef<Map<ModalType, ModalComponent>>(new Map());

  const preloadedModals = useModalsPreloading(PRELOADED_MODALS);

  useEffect(() => {
    preloadedModals.forEach((component, modalType) => {
      if (!modalsCache.current.has(modalType)) {
        modalsCache.current.set(modalType, component);
      }
    });
  }, [preloadedModals]);

  const loadModalComponent = useCallback(
    (modalType: ModalType): ModalComponent => {
      if (modalsCache.current.has(modalType)) {
        return modalsCache.current.get(modalType)!;
      }

      try {
        const loader = MODAL_LOADERS[modalType];
        if (!loader) {
          throw new Error(`No loader found for modal ${modalType}`);
        }

        const LazyComponent = React.lazy(loader);

        modalsCache.current.set(modalType, LazyComponent);
        return LazyComponent;
      } catch (error) {
        console.error(`Failed to load modal ${modalType}:`, error);
        throw error;
      }
    },
    []
  );

  const handleModal = useCallback<HandleModalFn>(
    (modal, params) => () => {
      if (modal == null) {
        setModals((state) => {
          if (!state.length) return [];

          addBreadcrumbModal(state[state.length - 1].name, 'close');

          const updated = state.slice(0, -1);

          const last = updated[updated.length - 1];

          if (last) {
            addUrlParameter('modal', last.name);
          } else {
            removeUrlParameters(['modal']);
          }

          return state.slice(0, -1);
        });
        return;
      }

      addBreadcrumbModal(
        typeof modal === 'string' ? modal : params?.name || 'custom modal',
        'open'
      );

      if (typeof modal === 'string') {
        if (!params?.stayInSameWindow) {
          addUrlParameter('modal', modal);
        }

        const modalType = modal as ModalType;
        const component = loadModalComponent(modalType);

        setModals((state) => {
          const newModal: ModalsState = {
            name: modal,
            modalType,
            component,
            props: params?.props,
          };

          return params?.stayInSameWindow ? [...state, newModal] : [newModal];
        });

        return;
      }

      if (params?.name) addUrlParameter('modal', params.name);

      setModals((state) =>
        params?.stayInSameWindow
          ? [
              ...state,
              {
                name: params?.name || 'custom',
                component: modal,
                props: params?.props,
              },
            ]
          : [
              {
                name: params?.name || '',
                component: modal,
                props: params?.props,
              },
            ]
      );
    },
    [loadModalComponent]
  );

  const handleClose = useCallback(() => {
    handleModal(undefined)();
  }, [handleModal]);

  useEffect(() => {
    if (!modals.length) {
      const modalParam = getQueryParameter('modal');
      const isNonAuthModal = nonAuthModalsMap.includes(modalParam || '');

      if (isAuthenticated && isNonAuthModal) {
        removeUrlParameters(['modal']);
        return;
      }

      const modalType =
        modalParam && Object.values(ModalType).includes(modalParam as ModalType)
          ? (modalParam as ModalType)
          : undefined;

      if (modalType && !pendingUser) {
        handleModal(modalType)();
      }
    }
  }, [handleModal, isAuthenticated, modals.length, pendingUser]);

  const modalComponent = useMemo(
    () =>
      modals.length ? (
        <>
          {modals.map(({ modalType, component: ModalContent, props }) => (
            <Modal key={modalType} onClose={handleClose}>
              {(modalProps) => (
                <ErrorBoundary fallback={<InternalError />}>
                  <Suspense fallback={<Spinner absoluteCenter size={64} />}>
                    <ModalContent {...modalProps} {...props} />
                  </Suspense>
                </ErrorBoundary>
              )}
            </Modal>
          ))}
        </>
      ) : null,
    [modals, handleClose]
  );

  return (
    <ModalsContext.Provider value={{ modalComponent, handleModal, modals }}>
      {children}
    </ModalsContext.Provider>
  );
};
